// Mukti Hospital ERP - NICU Offline Sync Engine
// NEW FILE: src/services/nicuSyncEngine.js
//
// Pushes offline-created NICU admissions and bills (queued in nicuOutbox) to
// the server once the network returns. Idempotent: every record carries a
// clientId and the server returns the existing record instead of creating a
// duplicate, so a retried push is always safe.
//
// Safety model (the server is the source of truth):
//   - clientId guard on /admission/add and /nicu-bills/generate prevents
//     duplicates on retry.
//   - the server's bed-occupied check rejects a double-booked bed on sync;
//     that entry is marked "failed" with the message so staff can retry with
//     a different bed.
//   - specialNo/regNo uniqueness rejects a duplicate bill.
//
// This engine never deletes an entry it failed to push; it only removes
// entries the server has accepted (or already has).

import axios from "axios";
import {
  getNicuOutbox,
  removeFromNicuOutbox,
  updateNicuOutboxStatus,
  removeNicuLocal,
} from "./nicuOutbox";

const API_BASE_URL = "https://api.muktihospital.com/api";
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const ENDPOINTS = {
  admission: `${API_BASE_URL}/nicu/admission/add`,
  bill: `${API_BASE_URL}/nicu/nicu-bills/generate`,
};

// Guard so overlapping triggers (online event + interval) don't run at once.
let isSyncing = false;

/**
 * Push every pending NICU outbox entry to the server, oldest first, with
 * admissions before bills. Safe to call repeatedly. Returns a small summary.
 */
export const syncNicuOutbox = async () => {
  if (isSyncing) return { skipped: true };
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    return { offline: true };
  }

  isSyncing = true;
  let synced = 0;
  let failed = 0;

  try {
    const entries = await getNicuOutbox();
    if (!entries.length) return { synced, failed };

    // Admissions before bills, then oldest first within each type. A bill may
    // depend on an admission created just before it in the same offline session.
    const order = { admission: 0, bill: 1 };
    entries.sort((a, b) => {
      const t = (order[a.type] ?? 9) - (order[b.type] ?? 9);
      if (t !== 0) return t;
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });

    for (const entry of entries) {
      const url = ENDPOINTS[entry.type];
      if (!url) {
        // Unknown type — leave it, but don't let it block the rest.
        continue;
      }

      try {
        await updateNicuOutboxStatus(entry.clientId, "syncing", null);
        // clientId travels inside payload; the server is idempotent on it.
        await axios.post(url, entry.payload, {
          headers: { "x-api-key": API_KEY },
        });
        // Success (either freshly created, or the server already had it).
        await removeFromNicuOutbox(entry.clientId);
        await removeNicuLocal(entry.clientId);
        synced++;
      } catch (err) {
        // A 4xx means the server rejected it for a real reason (bed occupied,
        // duplicate regNo, validation). Mark failed with the message; a network
        // error just leaves it pending for the next run.
        const status = err.response?.status;
        const serverMsg =
          err.response?.data?.error || err.message || "Sync failed";
        if (status && status >= 400 && status < 500) {
          await updateNicuOutboxStatus(entry.clientId, "failed", serverMsg);
          failed++;
        } else {
          // Network/5xx: keep it pending, stop this run (likely offline again).
          await updateNicuOutboxStatus(entry.clientId, "pending", serverMsg);
          break;
        }
      }
    }
  } catch (e) {
    console.warn("[NicuSync] syncNicuOutbox failed:", e);
  } finally {
    isSyncing = false;
  }

  // Tell the app something synced, so list pages can refresh themselves and
  if (synced > 0 && typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("nicu-synced", { detail: { synced, failed } })
    );
  }

  return { synced, failed };
};

let started = false;

/**
 * Start automatic syncing: once now (if online), on every "online" event, and
 * on a light interval as a heartbeat. Call once from app startup. Idempotent.
 */
export const startNicuSync = () => {
  if (started || typeof window === "undefined") return;
  started = true;

  const trigger = () => {
    syncNicuOutbox();
  };

  // Fire when the browser regains connectivity.
  window.addEventListener("online", trigger);

  // Heartbeat: every 60s, in case the "online" event was missed or the server
  // was down while navigator.onLine was already true.
  setInterval(trigger, 60000);

  // Try once shortly after startup (gives the app time to settle).
  setTimeout(trigger, 3000);
};

